import {
  QueryClient,
  QueryClientConfig,
  QueryClientProvider,
} from '@tanstack/react-query';
import { createContext, ReactNode, useContext } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type * as queries from '../api/queries';
import type * as workEnvironmentQueries from '../api/workEnvironmentQueries';
import type * as tenantFeatures from '../api/tenantFeatures';

export type ServicesContextProps = typeof queries &
  typeof workEnvironmentQueries &
  typeof tenantFeatures;
export type ServicesContextProviderProps = ServicesContextProps & {
  children?: ReactNode;
  client?: QueryClient;
  clientConfig?: QueryClientConfig;
};

const ServicesContext = createContext<ServicesContextProps | undefined>(
  undefined,
);

export const ServiceContextProvider = ({
  children,
  client,
  clientConfig,
  ...queries
}: ServicesContextProviderProps) => {
  const defaultQueryClientConfig: QueryClientConfig = {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
      },
      ...clientConfig?.defaultOptions,
    },
  };
  const queryClient = client ?? new QueryClient(defaultQueryClientConfig);

  return (
    <QueryClientProvider client={queryClient}>
      <ServicesContext.Provider value={{ ...queries }}>
        {children}
      </ServicesContext.Provider>
      <ReactQueryDevtools initialIsOpen={true} />
    </QueryClientProvider>
  );
};

export const useServicesContext = function (): ServicesContextProps {
  const context = useContext(ServicesContext);
  if (context === undefined) {
    throw new Error(
      'useServicesContext must be used within a ServicesContextProvider.',
    );
  }
  return context;
};
